import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import {
    MapPin,
    Clock,
    AlertCircle,
    Loader,
    CheckCircle,
    Navigation,
    ChevronRight,
    Zap,
    Calendar,
    Phone,
    Store
} from "lucide-react";

const Ordenes = () => {
    const { storeId } = useParams();
    const navigate = useNavigate();
    const [store, setStore] = useState(null);
    const [allStores, setAllStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [isNearestStore, setIsNearestStore] = useState(null);
    const [nearestStore, setNearestStore] = useState(null);
    const [orderTime, setOrderTime] = useState("immediate");
    const [scheduledTime, setScheduledTime] = useState("");
    const [availableTimes, setAvailableTimes] = useState([]);

    // Calculate distance using Haversine formula
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371e3;
        const φ1 = (lat1 * Math.PI) / 180;
        const φ2 = (lat2 * Math.PI) / 180;
        const Δφ = ((lat2 - lat1) * Math.PI) / 180;
        const Δλ = ((lon2 - lon1) * Math.PI) / 180;

        const a =
            Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    };

    // Format distance for display
    const formatDistance = (meters) => {
        if (meters < 1000) {
            return `${Math.round(meters)} m`;
        }
        return `${(meters / 1000).toFixed(1)} km`;
    };

    // Get current time in Hermosillo, Sonora (MST - UTC-7)
    const getHermosilloTime = () => {
        const now = new Date();
        const hermosillo = new Date(now.toLocaleString("en-US", { timeZone: "America/Hermosillo" }));
        return hermosillo;
    };

    // Generate available times
    useEffect(() => {
        const generateTimes = () => {
            const times = [];
            const now = getHermosilloTime();
            const currentHour = now.getHours();
            const currentMinutes = now.getMinutes();

            let startHour = currentHour;
            let startMinutes = Math.ceil(currentMinutes / 15) * 15;

            if (startMinutes >= 60) {
                startHour += 1;
                startMinutes = 0;
            }

            const endHour = 16;
            const endMinutes = 55;

            if (startHour > endHour || (startHour === endHour && startMinutes > endMinutes)) {
                setAvailableTimes([]);
                return;
            }

            for (let h = startHour; h <= endHour; h++) {
                const startMin = h === startHour ? startMinutes : 0;
                const endMin = h === endHour ? endMinutes : 45;

                for (let m = startMin; m <= endMin; m += 15) {
                    const timeString = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                    const displayTime = formatTime(timeString);
                    times.push({ value: timeString, display: displayTime });
                }
            }

            setAvailableTimes(times);
        };

        generateTimes();
        const interval = setInterval(generateTimes, 60000);
        return () => clearInterval(interval);
    }, []);

    // Format time for display (24h to 12h format)
    const formatTime = (time) => {
        const [hours, minutes] = time.split(':');
        const h = parseInt(hours);
        const period = h >= 12 ? 'PM' : 'AM';
        const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
        return `${displayHour}:${minutes} ${period}`;
    };

    // Fetch store and all stores data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const storeDoc = await getDoc(doc(db, "stores", storeId));
                if (!storeDoc.exists()) {
                    setError("Sucursal no encontrada");
                    setLoading(false);
                    return;
                }

                const storeData = { id: storeDoc.id, ...storeDoc.data() };
                setStore(storeData);

                const storesSnapshot = await getDocs(collection(db, "stores"));
                const storesData = [];
                storesSnapshot.forEach((doc) => {
                    storesData.push({ id: doc.id, ...doc.data() });
                });
                setAllStores(storesData);

                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const location = {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                            };
                            setUserLocation(location);

                            const storesWithDistance = storesData.map((s) => ({
                                ...s,
                                distance: calculateDistance(
                                    location.latitude,
                                    location.longitude,
                                    parseFloat(s.latitude),
                                    parseFloat(s.longitude)
                                ),
                            }));

                            const sorted = [...storesWithDistance].sort(
                                (a, b) => a.distance - b.distance
                            );
                            const nearest = sorted[0];
                            setNearestStore(nearest);
                            setIsNearestStore(nearest.id === storeId);
                        },
                        (error) => {
                            console.error("Error getting location:", error);
                        }
                    );
                }

                setLoading(false);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Error al cargar la información");
                setLoading(false);
            }
        };

        if (storeId) {
            fetchData();
        }
    }, [storeId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pt-16 sm:pt-20 flex items-center justify-center px-4">
                <div className="text-center bg-white rounded-2xl p-8 sm:p-12 shadow-lg w-full max-w-sm">
                    <div className="bg-gradient-to-br from-[#FF0000] to-[#CC0000] p-5 rounded-2xl inline-block mb-4">
                        <Loader className="w-10 h-10 sm:w-12 sm:h-12 text-white animate-spin" strokeWidth={2} />
                    </div>
                    <h3 className="text-gray-800 text-lg sm:text-xl font-bold mb-2">
                        Cargando
                    </h3>
                    <p className="text-gray-600 text-sm">Por favor espera...</p>
                </div>
            </div>
        );
    }

    if (error || !store) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pt-16 sm:pt-20 flex items-center justify-center px-4">
                <div className="text-center bg-white rounded-2xl p-8 sm:p-12 shadow-lg w-full max-w-sm">
                    <div className="bg-red-100 p-5 rounded-2xl inline-block mb-4">
                        <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-600" strokeWidth={2} />
                    </div>
                    <h3 className="text-gray-800 font-bold text-lg sm:text-xl mb-3">
                        {error || "Sucursal no encontrada"}
                    </h3>
                    <button
                        onClick={() => navigate("/sucursales")}
                        className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white px-6 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:shadow-lg transition-all w-full"
                    >
                        Ver Sucursales
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pt-16 sm:pt-20 pb-6 sm:pb-8">
            <div className="max-w-4xl mx-auto px-3 sm:px-4">
                {/* Store Header Card - Compacto */}
                <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-4 sm:mb-6">
                    {/* Store Photo con info overlay */}
                    {store.photo ? (
                        <div className="relative h-28 sm:h-32 md:h-36 overflow-hidden">
                            <img
                                src={store.photo}
                                alt={store.storeName}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                            <div className="absolute bottom-2 sm:bottom-3 left-3 sm:left-4 right-3 sm:right-4">
                                <h1 className="text-base sm:text-lg md:text-xl font-bold text-white drop-shadow-lg mb-1">
                                    {store.storeName}
                                </h1>
                                <div className="flex items-center text-white/90 text-xs sm:text-sm">
                                    <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 flex-shrink-0" strokeWidth={2} />
                                    <span className="line-clamp-1">{store.address}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gradient-to-br from-gray-100 to-gray-200 h-28 sm:h-32 md:h-36 flex items-center justify-center">
                            <Store className="w-12 h-12 sm:w-14 sm:h-14 text-gray-400" strokeWidth={1.5} />
                        </div>
                    )}

                    {/* Store Info Compacta - Grid Layout */}
                    <div className="p-3 sm:p-4">
                        {/* Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 mb-3">
                            {/* Dirección */}
                            <div className="flex items-center bg-gray-50 rounded-lg p-2 sm:p-2.5">
                                <div className="bg-[#FF0000] p-1.5 sm:p-2 rounded-lg mr-2 flex-shrink-0">
                                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" strokeWidth={2} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] sm:text-xs text-[#FF0000] font-bold uppercase">
                                        Dirección
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-700 line-clamp-1">
                                        {store.address}
                                    </p>
                                </div>
                            </div>

                            {/* Teléfono */}
                            <div className="flex items-center bg-gray-50 rounded-lg p-2 sm:p-2.5">
                                <div className="bg-[#FF0000] p-1.5 sm:p-2 rounded-lg mr-2 flex-shrink-0">
                                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" strokeWidth={2} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] sm:text-xs text-[#FF0000] font-bold uppercase">
                                        Teléfono
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-700 font-medium">
                                        {store.phoneNumber}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Location Status Alert - Compacto */}
                        {isNearestStore !== null && (
                            <div className={`p-2.5 sm:p-3 rounded-lg flex items-center gap-2 sm:gap-2.5 ${isNearestStore
                                    ? "bg-green-50 border-l-4 border-green-500"
                                    : "bg-amber-50 border-l-4 border-amber-500"
                                }`}>
                                {isNearestStore ? (
                                    <>
                                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" strokeWidth={2} />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-green-800 text-xs sm:text-sm">
                                                ¡Sucursal más cercana!
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" strokeWidth={2} />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-amber-800 text-xs sm:text-sm mb-0.5">
                                                <strong>{nearestStore?.storeName}</strong> está más cerca
                                            </p>
                                            <p className="text-[10px] sm:text-xs text-amber-700">
                                                A {formatDistance(nearestStore?.distance)} de distancia
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => navigate(`/order/${nearestStore?.id}`)}
                                            className="flex items-center bg-amber-600 hover:bg-amber-700 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-[10px] sm:text-xs font-bold transition-all flex-shrink-0"
                                        >
                                            <Navigation className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" strokeWidth={2} />
                                            Cambiar
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Order Time Selection Card */}
                <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-4 sm:mb-6">
                    <div className="p-4 sm:p-6">
                        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center">
                            <Clock className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-[#FF0000]" strokeWidth={2} />
                            ¿Cuándo lo quieres?
                        </h2>

                        <div className="space-y-3 sm:space-y-4">
                            {/* Immediate Option */}
                            <button
                                onClick={() => setOrderTime("immediate")}
                                className={`w-full p-3 sm:p-4 rounded-xl flex items-center justify-between transition-all ${orderTime === "immediate"
                                        ? "bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white shadow-lg ring-2 ring-[#FFBC00]"
                                        : "bg-gray-50 text-gray-800 hover:bg-gray-100 border-2 border-transparent hover:border-gray-200"
                                    }`}
                            >
                                <div className="flex items-center flex-1">
                                    <div className={`p-2 sm:p-2.5 rounded-xl mr-3 ${orderTime === "immediate" ? "bg-white/20" : "bg-white"
                                        }`}>
                                        <Zap className={`w-5 h-5 sm:w-6 sm:h-6 ${orderTime === "immediate" ? "text-[#FFBC00]" : "text-[#FF0000]"
                                            }`} strokeWidth={2} />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm sm:text-base md:text-lg">
                                            Lo antes posible
                                        </p>
                                        <p className={`text-xs sm:text-sm ${orderTime === "immediate" ? "text-white/90" : "text-gray-600"
                                            }`}>
                                            20-30 minutos aproximadamente
                                        </p>
                                    </div>
                                </div>
                                {orderTime === "immediate" && (
                                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFBC00] flex-shrink-0" strokeWidth={2.5} />
                                )}
                            </button>

                            {/* Scheduled Option */}
                            <button
                                onClick={() => setOrderTime("scheduled")}
                                className={`w-full p-3 sm:p-4 rounded-xl flex items-center justify-between transition-all ${orderTime === "scheduled"
                                        ? "bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white shadow-lg ring-2 ring-[#FFBC00]"
                                        : "bg-gray-50 text-gray-800 hover:bg-gray-100 border-2 border-transparent hover:border-gray-200"
                                    }`}
                            >
                                <div className="flex items-center flex-1">
                                    <div className={`p-2 sm:p-2.5 rounded-xl mr-3 ${orderTime === "scheduled" ? "bg-white/20" : "bg-white"
                                        }`}>
                                        <Calendar className={`w-5 h-5 sm:w-6 sm:h-6 ${orderTime === "scheduled" ? "text-[#FFBC00]" : "text-[#FF0000]"
                                            }`} strokeWidth={2} />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm sm:text-base md:text-lg">
                                            Programar pedido
                                        </p>
                                        <p className={`text-xs sm:text-sm ${orderTime === "scheduled" ? "text-white/90" : "text-gray-600"
                                            }`}>
                                            Elige tu hora (hasta las 4:55 PM)
                                        </p>
                                    </div>
                                </div>
                                {orderTime === "scheduled" && (
                                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFBC00] flex-shrink-0" strokeWidth={2.5} />
                                )}
                            </button>

                            {/* Time Picker */}
                            {orderTime === "scheduled" && (
                                <div className="bg-gray-50 rounded-xl p-3 sm:p-4 animate-slideIn">
                                    <label className="block text-gray-800 font-bold mb-3 text-sm sm:text-base">
                                        Selecciona la hora de entrega
                                    </label>
                                    {availableTimes.length > 0 ? (
                                        <select
                                            value={scheduledTime}
                                            onChange={(e) => setScheduledTime(e.target.value)}
                                            className="w-full p-3 sm:p-4 bg-white text-gray-800 font-semibold rounded-xl border-2 border-gray-200 focus:outline-none focus:border-[#FF0000] focus:ring-2 focus:ring-[#FF0000]/20 transition-all text-sm sm:text-base"
                                        >
                                            <option value="">Selecciona un horario...</option>
                                            {availableTimes.map((time) => (
                                                <option key={time.value} value={time.value}>
                                                    {time.display}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="bg-white rounded-xl p-4 sm:p-6 text-center border-2 border-amber-200">
                                            <div className="bg-amber-100 p-3 rounded-xl inline-block mb-3">
                                                <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-amber-600" strokeWidth={2} />
                                            </div>
                                            <p className="text-amber-800 font-bold text-sm sm:text-base mb-1">
                                                Ya no hay horarios disponibles hoy
                                            </p>
                                            <p className="text-gray-600 text-xs sm:text-sm">
                                                Regresa mañana o elige "Lo antes posible"
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Menu Section */}
                <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                    <div className="p-4 sm:p-6">
                        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
                            Menú del día
                        </h2>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 sm:p-12 text-center">
                            <div className="bg-gradient-to-br from-[#FF0000] to-[#CC0000] p-5 rounded-2xl inline-block mb-4">
                                <Clock className="w-10 h-10 sm:w-12 sm:h-12 text-white" strokeWidth={2} />
                            </div>
                            <h3 className="text-gray-800 font-bold text-base sm:text-lg md:text-xl mb-2">
                                Próximamente disponible
                            </h3>
                            <p className="text-gray-600 text-sm sm:text-base">
                                Estamos preparando el menú para ti
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-slideIn {
                    animation: slideIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default Ordenes;